/**
 * 把 AI 回答的 Markdown 清洗成"可以直接念出来"的纯文本。
 *
 * 回答正文里混了三类不该念的东西：
 * 1. Markdown 语法本身（#、**、表格竖线…）
 * 2. 本项目的渲染占位符：`<!-- chart id="..." -->`、`[CHART_xxx]`、`[LINKREF_1,2]`
 *    （见 src/lib/utils/chart-placeholder-parser.ts 与 MessageBox 的渲染逻辑）
 * 3. 代码块 / 图表 JSON —— 整段跳过，念出来只是噪音
 */

/** 代码块整段丢弃（含 ECharts 配置 JSON） */
const FENCED_CODE_REGEX = /```[\s\S]*?```|~~~[\s\S]*?~~~/g;
/** HTML 注释形式的占位符（chart / market card 等） */
const HTML_COMMENT_REGEX = /<!--[\s\S]*?-->/g;
/** 未闭合的注释尾巴（流式输出时会出现） */
const DANGLING_COMMENT_REGEX = /<!--[\s\S]*$/;
const CHART_PLACEHOLDER_REGEX = /`?\[CHART_[^\]]*\]`?/g;
/** [LINKREF_1,2] 及其 HTML 实体转义变体 */
const LINKREF_REGEX = /\[LINKREF(?:_|&#95;)[\d,\s]*\]/g;
/** Markdown 图片：整体丢弃（alt 一般是文件名，念了没意义） */
const IMAGE_REGEX = /!\[[^\]]*\]\([^)]*\)/g;
/** Markdown 链接：只保留链接文字 */
const LINK_REGEX = /\[([^\]]*)\]\([^)]*\)/g;
const HTML_TAG_REGEX = /<\/?[a-zA-Z][^>]*>/g;
/** 裸 URL */
const BARE_URL_REGEX = /\bhttps?:\/\/\S+/g;
// 行首类规则一律用 [ \t] 而不是 \s：多行模式下 \s 会吃掉空行，
// 段落边界（\n\n）没了之后分句就没法按段落切了。
/** 行首标题 / 引用 / 列表符号 */
const HEADING_REGEX = /^[ \t]{0,3}#{1,6}[ \t]+/gm;
const BLOCKQUOTE_REGEX = /^[ \t]{0,3}>+[ \t]?/gm;
const LIST_MARKER_REGEX = /^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm;
/** 分隔线 */
const THEMATIC_BREAK_REGEX = /^[ \t]{0,3}(?:[-*_][ \t]*){3,}$/gm;
/** 表格分隔行 |---|---| */
const TABLE_DIVIDER_REGEX = /^[ \t]*\|?[ \t:|-]*\|[ \t:|-]*$/gm;
/** 行内代码、加粗、斜体、删除线的标记符号 */
const INLINE_CODE_REGEX = /`([^`\n]+)`/g;
const EMPHASIS_REGEX = /(\*\*\*|\*\*|\*|___|__|_|~~)(?=\S)([\s\S]*?\S)\1/g;
/** 脚注式引用角标 [1] [2,3]，紧跟在文字后面时不该念 */
const CITATION_MARKER_REGEX = /\[\d+(?:\s*,\s*\d+)*\]/g;

/** 表格行转成"单元格 A，单元格 B"，保留信息又不念竖线 */
function flattenTableRow(line: string): string {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return line;

  const cells = trimmed
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
    .filter(Boolean);

  return cells.length > 0 ? `${cells.join('，')}。` : '';
}

/**
 * 清洗 Markdown / 占位符，返回适合 TTS 的纯文本。
 * 纯函数，无副作用，服务端与客户端通用。
 */
export function stripMarkdownForSpeech(markdown: string): string {
  if (!markdown?.trim()) {
    return '';
  }

  let text = markdown;

  // 1. 整段丢弃的内容
  text = text.replace(FENCED_CODE_REGEX, '\n');
  text = text.replace(HTML_COMMENT_REGEX, '');
  text = text.replace(DANGLING_COMMENT_REGEX, '');
  text = text.replace(IMAGE_REGEX, '');

  // 2. 本项目自定义占位符
  text = text.replace(CHART_PLACEHOLDER_REGEX, '');
  text = text.replace(LINKREF_REGEX, '');

  // 3. 链接 / 标签 / URL
  text = text.replace(LINK_REGEX, '$1');
  text = text.replace(HTML_TAG_REGEX, '');
  text = text.replace(BARE_URL_REGEX, '');

  // 4. 块级语法
  text = text.replace(THEMATIC_BREAK_REGEX, '');
  text = text.replace(TABLE_DIVIDER_REGEX, '');
  text = text.replace(HEADING_REGEX, '');
  text = text.replace(BLOCKQUOTE_REGEX, '');
  text = text.replace(LIST_MARKER_REGEX, '');
  text = text
    .split('\n')
    .map(flattenTableRow)
    .join('\n');

  // 5. 行内语法
  text = text.replace(INLINE_CODE_REGEX, '$1');
  text = text.replace(EMPHASIS_REGEX, '$2');
  text = text.replace(CITATION_MARKER_REGEX, '');

  // 6. 收敛空白：段落之间保留一个空行，供分句时识别段落边界
  text = text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
}

/** 供 UI 判断"这段内容有没有可朗读的实质文字" */
export function hasSpeakableText(markdown: string): boolean {
  const text = stripMarkdownForSpeech(markdown);
  return /[\p{L}\p{N}]/u.test(text);
}
